import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { Controller, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NameField } from './NameField';
import { SwitchRow } from './SwitchRow';
import type { MakeFormValues, ModelFormValues } from '../_lib/catalogForms';
import type { EntityForm } from '../_lib/useEntityForm';

/**
 * Name + flag switches of a make or a model (AUT-437). The rename hint says
 * the old name stays searchable as an alias (the API adds a `rename` alias).
 */
type Flag = 'popular' | 'onSale' | 'legacy' | 'hidden';

export interface EntityFieldsProps<V extends MakeFormValues | ModelFormValues> {
  entity: EntityForm<V>;
  currentName: string;
  flags: readonly Flag[];
  disabled: boolean;
  currentMakeId: string;
  onOpenMake: (makeId: string) => void;
}

export const EntityFields = <V extends MakeFormValues | ModelFormValues>({
  entity,
  currentName,
  flags,
  disabled,
  currentMakeId,
  onOpenMake,
}: EntityFieldsProps<V>): ReactElement => {
  const { t } = useTranslation();
  const { form } = entity;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
      <Controller
        control={form.control}
        name={'name' as Path<V>}
        render={({ field, fieldState }) => {
          const value = String(field.value ?? '');
          const renamed = value.trim() !== '' && value.trim() !== currentName;
          return (
            <NameField
              ref={field.ref}
              value={value}
              onBlur={field.onBlur}
              onChange={(next) => {
                entity.clearServerError();
                field.onChange(next);
              }}
              label={t('vehicleCatalog.name.label')}
              errorKey={fieldState.error?.message}
              serverError={entity.serverError}
              onOpenMake={onOpenMake}
              currentMakeId={currentMakeId}
              disabled={disabled}
              helper={
                renamed
                  ? t('vehicleCatalog.drawer.renamePending', { old: currentName })
                  : t('vehicleCatalog.drawer.renameHint')
              }
            />
          );
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {flags.map((flag) => (
          <Controller
            key={flag}
            control={form.control}
            name={flag as Path<V>}
            render={({ field }) => (
              <SwitchRow
                label={t(`vehicleCatalog.toggles.${flag}`)}
                hint={t(`vehicleCatalog.toggles.${flag}Hint`)}
                checked={Boolean(field.value)}
                disabled={disabled}
                onChange={field.onChange}
              />
            )}
          />
        ))}
      </Box>
    </Box>
  );
};
